/**
 * Utilities for parsing OAuth URLs from chat messages
 */

import { parseConnectionCardUrl } from '@littlebird/ui-utils';

import { getEnvVar } from '@/env';

export { isPartialConnectionFlowRef } from '@littlebird/ui-utils';

let apiOrigin: string | undefined;

export interface ParsedOAuthInfo {
  isOAuthUrl: boolean;
  flowRef?: string;
  provider?: 'composio' | 'mcp';
  toolkitName?: string;
  displayName?: string;
  accountLabel?: string;
  accountId?: string;
  serverId?: string;
  flowId?: string;
  redirectUrl?: string;
}

/**
 * Parses an OAuth URL and extracts integration information
 * @param href The URL to parse
 * @returns Parsed OAuth information
 */
export function parseOAuthUrl(href: string): ParsedOAuthInfo {
  const connectionCard = parseConnectionCardUrl(href);
  if (connectionCard.kind === 'connectionCard') {
    return { isOAuthUrl: true, flowRef: connectionCard.flowRef };
  }

  try {
    const url = new URL(href);
    // A card link the shared parser rejected stays rejected: its own redirect is not trusted
    if (url.protocol === 'littlebird:') return { isOAuthUrl: false };

    apiOrigin ??= new URL(getEnvVar('VITE_API_URL')).origin;

    // TODO: tight coupling to URL here, less than ideal but works for now
    // until we have a better way to pass metadata to messages
    if (
      url.origin !== apiOrigin ||
      !url.pathname.includes('/integrations/authorize/')
    ) {
      return { isOAuthUrl: false };
    }

    // Extract toolkit name and display name from query params
    // Structure: /integrations/authorize/{redirection_key}?toolkit_name=gmail&display_name=Gmail
    // Note: display_name should be URL-encoded if it contains spaces
    const toolkitName = url.searchParams.get('toolkit_name') || undefined;
    const displayName = url.searchParams.get('display_name') || undefined;
    const accountLabel = url.searchParams.get('account_label') || undefined;
    const accountId = url.searchParams.get('account_id') || undefined;
    const flowId = url.searchParams.get('flow_id') || undefined;

    // Create clean redirect URL without the toolkit params
    const cleanUrl = new URL(href);
    cleanUrl.searchParams.delete('toolkit_name');
    cleanUrl.searchParams.delete('display_name');
    cleanUrl.searchParams.delete('account_label');
    cleanUrl.searchParams.delete('account_id');
    cleanUrl.searchParams.delete('flow_id');
    const cleanRedirectUrl = cleanUrl.toString();

    const result = {
      isOAuthUrl: true,
      provider: 'composio' as const,
      toolkitName: toolkitName || 'unknown',
      displayName,
      accountLabel: accountLabel || '',
      accountId,
      flowId,
      redirectUrl: cleanRedirectUrl,
    };

    return result;
  } catch (_error) {
    return { isOAuthUrl: false };
  }
}
